import { BaseTextMergeElement } from "../BaseTextMergeElement";
import { DataTypes } from "../../../Common/Data/DataTypes";
import { TextMergeConfiguration } from "../../TextMergeConfiguration";
import { ExecutionData } from "../../../Common/Data/ExecutionData/ExecutionData";
import { ITestingContext } from "../../../Common/Testing/ITestingContext";
import { TextMergeType } from "../../TextMergeType";
import { ExpressionConfiguration } from "../../../Expressions/ExpressionConfiguration";
import { StateManager } from "../../../Common/StateManager";
import { IDataDefinition } from "../../../Common/Data/Definitions/IDataDefinition";
import { DataUtilities } from "../../../Common/DataUtilities";
import { ExpressionValueType } from "../../../Expressions/ExpressionValueType";
import type { AppGlueAny } from "../../../Common/AppGlueAny";

let _any: AppGlueAny;

export abstract class BaseJSONXMLElement extends BaseTextMergeElement {
    abstract readonly outputType: DataTypes;

    constructor(fieldName?: string) {
        super();
        if (fieldName) this._fieldName = fieldName;
    }

    private _fieldName: string = "FieldName";

    get fieldName(): string {
        return this._fieldName;
    }

    set fieldName(value: string) {
        this._fieldName = value;
        StateManager.propertyChanged(this, "fieldName");
    }

    private _list: boolean = false;

    get list(): boolean {
        return this._list;
    }

    set list(value: boolean) {
        this._list = value;
        StateManager.propertyChanged(this, "list");
    }

    private _value: ExpressionConfiguration = new ExpressionConfiguration();

    get value(): ExpressionConfiguration {
        this._value.inputData = () => this.getInputData();
        let dd: IDataDefinition;
        dd = DataUtilities.createDataDefinitionFromDataType(this.outputType);

        dd.list = this.list;

        this._value.executionResult = dd;

        return this._value;
    }

    set value(value: ExpressionConfiguration) {
        this._value = value;
        StateManager.propertyChanged(this, "value");
    }

    canAdd(config: TextMergeConfiguration): boolean {
        return config.type === TextMergeType.JSON || config.type === TextMergeType.XML;
    }

    output(data: ExecutionData, config: TextMergeConfiguration, testContext: ITestingContext | undefined): string {
        let defval = data.get(TextMergeConfiguration.DEFAULT_VALUE_DATA_NAME);
        let val = defval;
        if (this.value.rootExpression.expressionRoot.valueType !== ExpressionValueType.UNSET) {
            try {
                val = this.value.run(data, testContext);
            } catch (e: AppGlueAny) {
                if (e.message.indexOf("is missing") !== -1) val = "[Error: " + e.message + "]";

                if (!defval || (defval + "").trim().length === 0) return "";
            }
        } else {
            if (!defval || (defval + "").trim().length === 0) return "";
        }

        if (config.type === TextMergeType.XML) {
            if (this.__type === "jsonobject") {
                let stringVal = DataUtilities.objectToXML(val);

                stringVal = stringVal.replace("\n", DataUtilities.getIndent(this.getDepth()));

                return (
                    DataUtilities.getIndent(this.getDepth()) +
                    "<" +
                    this._fieldName +
                    ">" +
                    stringVal +
                    "</" +
                    this._fieldName +
                    ">\n"
                );
            } else {
                return (
                    DataUtilities.getIndent(this.getDepth()) +
                    "<" +
                    this._fieldName +
                    ">" +
                    val +
                    "</" +
                    this._fieldName +
                    ">\n"
                );
            }
        } else {
            // JSON
            return DataUtilities.getIndent(this.getDepth()) + '"' + this._fieldName + '"' + ": " + JSON.stringify(val);
        }
    }

    override getStorageData(): object {
        let d = super.getStorageData();

        Reflect.set(d, "fieldName", this._fieldName);
        Reflect.set(d, "list", this._list);
        Reflect.set(d, "value", this._value.getStorageData());

        return d;
    }

    override setStorageData(d: object) {
        super.setStorageData(d);

        this._fieldName = Reflect.get(d, "fieldName");
        this._list = Reflect.get(d, "list");
        this._value.setStorageData(Reflect.get(d, "value"));
    }
}
